import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMembershipsCatalog } from "../hooks/useMembershipsCatalog";
import { MembershipOffering } from "../api/contentful";
import { createMembershipPurchaseSession } from "../api/membershipPurchase";
import LoadingSpinner from "../components/LoadingSpinner";
import "./MembershipDetailsPage.css";

const MembershipDetailsPage = () => {
  const { data, loading, error } = useMembershipsCatalog();
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [membership, setMembership] = useState<MembershipOffering | null>(null);
  const [isRedirectingToPayment, setIsRedirectingToPayment] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [paymentNotice, setPaymentNotice] = useState<
    "success" | "cancelled" | null
  >(null);

  useEffect(() => {
    const match = data?.memberships.find((item) => item.slug === slug);
    if (match) {
      setMembership(match);
    }
  }, [slug, data]);

  useEffect(() => {
    const status = searchParams.get("payment");
    if (status !== "success" && status !== "cancelled") return;

    setPaymentNotice(status);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  async function handleBuyNow() {
    if (!membership || isRedirectingToPayment) return;

    setIsRedirectingToPayment(true);
    setCheckoutError(null);

    try {
      const session = await createMembershipPurchaseSession(
        membership.contentfulId,
      );
      if (!session.checkoutUrl) {
        setCheckoutError("Could not create payment session. Please try again.");
        setIsRedirectingToPayment(false);
        return;
      }
      window.location.assign(session.checkoutUrl);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not start payment. Please try again.";
      setCheckoutError(message);
      setIsRedirectingToPayment(false);
    }
  }

  if (loading) {
    return <LoadingSpinner className="loading-spinner-container--fullscreen" />;
  }
  if (error) return <p>Could not load membership. {error}</p>;
  if (!data) return <p>No membership data found.</p>;

  const activeMembership = membership ? (
    <div className="content-container product-details-container">
      <div className="product-details-images-container">
        {membership.coverImage && (
          <img
            src={membership.coverImage}
            alt={membership.name}
            className="product-details-cover-image"
          />
        )}
      </div>
      <div className="product-details-info">
        <h3>{membership.name}</h3>
        <div>
          {membership.description && <p>{membership.description}</p>}
          <p className="bold-capitalized">Price: {membership.price} SEK</p>
        </div>
        {checkoutError ? <p>{checkoutError}</p> : null}
        <button
          type="button"
          className="btn"
          disabled={isRedirectingToPayment || membership.price <= 0}
          onClick={handleBuyNow}
        >
          {isRedirectingToPayment ? "Redirecting..." : "Buy Now"}
        </button>
      </div>
    </div>
  ) : (
    <p>Membership not found.</p>
  );

  return (
    <div>
      {paymentNotice === "success" && (
        <div className="content-container ">
          <div className="membership-payment-message membership-payment-message-success">
            <p>
              <strong>Thank you!</strong> Your payment was received and your
              membership is confirmed. A confirmation email is on its way.
            </p>
          </div>
        </div>
      )}
      {paymentNotice === "cancelled" && (
        <div className="content-container membership-payment-message membership-payment-message-cancelled">
          <p>Payment was cancelled. You can try again when you are ready.</p>
        </div>
      )}
      {activeMembership}
    </div>
  );
};

export default MembershipDetailsPage;
